class SlideshowSlider extends HTMLElement {

  static get observedAttributes() {
    return ['data-autoplay', 'data-autoplay-speed'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.track) {
      this._init();
    }
  }

  connectedCallback() {
    this.track = this.querySelector('.slider-track');
    if (!this.track) return;
    if (!this._hookAttached) {
      this._hookAttached = true;
      this.addEventListener('init-slideshow', () => this._init());
    }
    this._init();
  }

  disconnectedCallback() {
    this._ac?.abort();
    clearInterval(this._ticker);
    clearTimeout(this._scrollTimer);
  }

  _init() {
    this._ac?.abort();
    clearInterval(this._ticker);

    // Remove clones from any previous init
    this.track.querySelectorAll('[data-clone]').forEach(n => n.remove());

    const real = [...this.track.querySelectorAll('.slide')];
    if ((this._n = real.length) < 2) return;

    this._isLoop = this.dataset.autoplay === 'true';

    // Only stamp clone sets when autoplay (loop) is enabled
    if (this._isLoop) {
      const stamp = slides => {
        const f = document.createDocumentFragment();
        slides.forEach(s => { const c = s.cloneNode(true); c.dataset.clone = ''; f.appendChild(c); });
        return f;
      };
      this.track.insertBefore(stamp(real), this.track.firstChild);
      this.track.appendChild(stamp(real));
    }

    // Build pagination buttons (JS-owned)
    this._buildPagination(this._n);

    // Reset interaction state on (re)init
    this._hovered  = false;
    this._dragging = false;
    this._fixing   = false;
    this._cur      = 0;
    this.track.scrollLeft = this._isLoop ? this._n * this._w() : 0;
    this._setPagination(0);

    // Bind events via AbortController for clean teardown
    const { signal } = (this._ac = new AbortController());

    this.track.addEventListener('scroll', () => this._onScroll(), { passive: true, signal });
    this.track.addEventListener('pointerdown',   () => { this._dragging = true;  clearInterval(this._ticker); }, { signal });
    this.track.addEventListener('pointerup',     () => { this._dragging = false; this._restartAutoplay(); }, { signal });
    this.track.addEventListener('pointercancel', () => { this._dragging = false; this._restartAutoplay(); }, { signal });
    // Pause on hover — applies on desktop
    this.addEventListener('mouseenter', () => { this._hovered = true;  clearInterval(this._ticker); }, { signal });
    this.addEventListener('mouseleave', () => { this._hovered = false; this._restartAutoplay(); }, { signal });

    this._pag?.addEventListener('click', e => {
      const i = +e.target.closest('button')?.dataset.index;
      if (!isNaN(i)) { this.goToSlide(i); this._restartAutoplay(); }
    }, { signal });

    // Listen for Shopify Theme Editor block selection
    document.addEventListener('shopify:block:select', e => {
      if (!this.contains(e.target)) return;
      const slides = [...this.track.querySelectorAll('.slide:not([data-clone])')];
      const index = slides.indexOf(e.target.closest('.slide'));
      if (index !== -1) {
        this.goToSlide(index);
        clearInterval(this._ticker);
      }
    }, { signal });

    this._restartAutoplay();
  }

  /* ── Helpers ─────────────────────────────────────────────── */

  _w() { return this.track.clientWidth; }

  goToSlide(i, smooth = true) {
    const left = (this._isLoop ? this._n + i : i) * this._w();
    smooth ? this.track.scrollTo({ left, behavior: 'smooth' }) : (this.track.scrollLeft = left);
    this._cur = i;
    this._setPagination(i);
  }

  _next() {
    const w = this._w();
    if (this._isLoop) {
      this.track.scrollTo({ left: Math.round(this.track.scrollLeft / w) * w + w, behavior: 'smooth' });
    } else {
      const nextIdx = Math.min(this._n - 1, this._cur + 1);
      this.goToSlide(nextIdx);
    }
  }

  /* ── Scroll handler + loop correction ────────────────────── */

  _onScroll() {
    if (this._fixing) return;
    const w = this._w();
    if (!w) return;

    if (this._isLoop) {
      // Map current DOM-index to real slide index via modulo
      const dom = Math.round(this.track.scrollLeft / w);
      const idx = dom % this._n;
      if (idx !== this._cur) { this._cur = idx; this._setPagination(idx); }

      // After scroll settles, silently teleport back to real-slide zone if needed.
      // Direct scrollLeft assignment is always instant — immune to CSS scroll-behavior.
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        const d = Math.round(this.track.scrollLeft / w);
        if (d < this._n || d >= this._n * 2) {
          this._fixing = true;
          this.track.scrollLeft += (d < this._n ? 1 : -1) * this._n * w;
          requestAnimationFrame(() => { this._fixing = false; });
        }
      }, 80);
    } else {
      // Linear non-looping scroll: calculate index directly
      const dom = Math.round(this.track.scrollLeft / w);
      const idx = Math.max(0, Math.min(this._n - 1, dom));
      if (idx !== this._cur) {
        this._cur = idx;
        this._setPagination(idx);
      }
    }
  }

  /* ── Autoplay ─────────────────────────────────────────────── */

  _restartAutoplay() {
    clearInterval(this._ticker);
    // Don't restart while the user is hovering or actively dragging
    if (this._hovered || this._dragging) return;
    if (this._isLoop && this.dataset.autoplay === 'true') {
      this._ticker = setInterval(() => this._next(), +this.dataset.autoplaySpeed || 5000);
    }
  }

  /* ── Pagination ───────────────────────────────────────────── */

  _buildPagination(n) {
    this._pag = this.querySelector('.pagination');
    if (!this._pag) return;
    this._pag.innerHTML = '';
    this._btns = Array.from({ length: n }, (_, i) => {
      const b = document.createElement('button');
      b.type = 'button'; b.dataset.index = i;
      b.setAttribute('aria-label', `Slide ${i + 1}`);
      return this._pag.appendChild(b);
    });
  }

  _setPagination(i) {
    this._btns?.forEach((b, j) => {
      b.classList.toggle('is-active', j === i);
      b.setAttribute('aria-current', j === i ? 'true' : 'false');
    });
  }
}

customElements.define('slideshow-slider', SlideshowSlider);
